import type { Sample, SampleWorkflowStatus } from "../types/crm";

export const sampleStatusLabels: Record<SampleWorkflowStatus, string> = {
  draft: "草稿", submitted_to_engineering: "已提交工程", engineering_review: "工程审核", needs_business_info: "待业务补资料", business_resubmitted: "业务已补充", engineering_approved: "工程通过", engineering_rejected: "技术不可行", submitted_to_factory: "已提交工厂", factory_review: "工厂审核", factory_needs_change: "工厂要求变更", factory_approved: "工厂确认", material_pending: "等待原料", packaging_pending: "等待包材", production_queued: "待排产", scheduled: "已排产", in_production: "生产中", production_exception: "生产异常", production_completed: "生产完成", qc_pending: "待质检", qc_in_progress: "质检中", qc_failed: "质检失败", qc_conditional_pass: "质检有条件通过", qc_passed: "质检通过", ready_to_ship: "待寄样", shipped: "已寄样", in_transit: "运输中", delivered: "已签收", awaiting_customer_feedback: "待客户反馈", feedback_received: "已收反馈", needs_adjustment: "需要微调", needs_new_version: "需要新版本", customer_approved: "客户确认", converted_to_opportunity: "已转商机", converted_to_order: "已转订单", paused: "已暂停", cancelled: "已取消", closed: "已完成",
};

export const sampleTransitions: Record<SampleWorkflowStatus, SampleWorkflowStatus[]> = {
  draft: ["submitted_to_engineering", "cancelled"],
  submitted_to_engineering: ["engineering_review", "needs_business_info", "engineering_rejected"],
  engineering_review: ["needs_business_info", "engineering_approved", "engineering_rejected"],
  needs_business_info: ["business_resubmitted", "cancelled"],
  business_resubmitted: ["engineering_review", "needs_business_info", "engineering_approved"],
  engineering_approved: ["submitted_to_factory", "needs_adjustment"],
  engineering_rejected: ["needs_adjustment", "cancelled", "closed"],
  submitted_to_factory: ["factory_review"],
  factory_review: ["factory_needs_change", "factory_approved", "material_pending", "packaging_pending"],
  factory_needs_change: ["engineering_review", "business_resubmitted", "cancelled"],
  factory_approved: ["production_queued", "material_pending", "packaging_pending"],
  material_pending: ["production_queued", "paused", "cancelled"],
  packaging_pending: ["production_queued", "paused", "cancelled"],
  production_queued: ["scheduled", "paused"],
  scheduled: ["in_production", "paused"],
  in_production: ["production_exception", "production_completed"],
  production_exception: ["in_production", "engineering_review", "cancelled"],
  production_completed: ["qc_pending"],
  qc_pending: ["qc_in_progress"],
  qc_in_progress: ["qc_failed", "qc_conditional_pass", "qc_passed"],
  qc_failed: ["engineering_review", "production_queued", "cancelled"],
  qc_conditional_pass: ["ready_to_ship", "production_queued"],
  qc_passed: ["ready_to_ship"],
  ready_to_ship: ["shipped"],
  shipped: ["in_transit", "delivered"],
  in_transit: ["delivered"],
  delivered: ["awaiting_customer_feedback"],
  awaiting_customer_feedback: ["feedback_received"],
  feedback_received: ["needs_adjustment", "needs_new_version", "customer_approved"],
  needs_adjustment: ["needs_new_version", "engineering_review"],
  needs_new_version: ["closed"],
  customer_approved: ["converted_to_opportunity", "converted_to_order", "closed"],
  converted_to_opportunity: ["closed"],
  converted_to_order: ["closed"],
  paused: ["engineering_review", "factory_review", "production_queued", "cancelled"],
  cancelled: [],
  closed: [],
};

export const sampleProgress = [
  { label: "申请", statuses: ["draft"] },
  { label: "工程审核", statuses: ["submitted_to_engineering", "engineering_review", "needs_business_info", "business_resubmitted", "engineering_approved", "engineering_rejected"] },
  { label: "工厂确认", statuses: ["submitted_to_factory", "factory_review", "factory_needs_change", "factory_approved", "material_pending", "packaging_pending"] },
  { label: "生产", statuses: ["production_queued", "scheduled", "in_production", "production_exception", "production_completed"] },
  { label: "质检", statuses: ["qc_pending", "qc_in_progress", "qc_failed", "qc_conditional_pass", "qc_passed"] },
  { label: "寄样", statuses: ["ready_to_ship", "shipped", "in_transit", "delivered"] },
  { label: "客户反馈", statuses: ["awaiting_customer_feedback", "feedback_received", "needs_adjustment", "needs_new_version", "customer_approved"] },
  { label: "完成", statuses: ["converted_to_opportunity", "converted_to_order", "closed"] },
] as const;

export function getAllowedTransitions(status: SampleWorkflowStatus) {
  return sampleTransitions[status] || [];
}

export function sampleProgressIndex(status: SampleWorkflowStatus) {
  if (status === "cancelled" || status === "paused") return -1;
  return sampleProgress.findIndex((step) => (step.statuses as readonly string[]).includes(status));
}

export function sampleSla(sample: Sample) {
  if (!sample.due_at || ["closed", "cancelled"].includes(sample.status)) return { label: "无进行中 SLA", tone: "slate" as const };
  const hours = (new Date(sample.due_at).getTime() - Date.now()) / 3_600_000;
  if (hours < 0) return { label: `已超期 ${Math.ceil(Math.abs(hours))} 小时`, tone: "red" as const };
  if (hours < 24) return { label: `剩余 ${Math.ceil(hours)} 小时`, tone: "amber" as const };
  return { label: `剩余 ${Math.ceil(hours / 24)} 天`, tone: "green" as const };
}
